import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useQuery } from "@tanstack/react-query";
import { useCriticalAlerts } from "@/lib/queries";
import { get } from "@/lib/api";
import { Search, Bell, Moon, Sun, LogOut, ChevronDown, Menu, MapPin, LifeBuoy } from "lucide-react";
import { scopeLine } from "@/pages/RoleHome";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTheme } from "next-themes";

const CRITICAL_ROLES = ["PATHOLOGIST", "SENIOR_TECHNICIAN", "HUB_ADMIN", "QUALITY_MANAGER"];

export function TopHeader({ onMenu }) {
  const { session, logout, hubId, setHubId, can, allowed } = useApp();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { const el = document.getElementById("global-search"); if (el) { e.preventDefault(); el.focus(); el.select(); } }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // The hub selector exists only for roles that span more than one hub
  // (network and city scope); everyone else sees where they are instead -
  // and does not load the hub list at all (a partner radiology centre has no
  // business with it). Same cache key as useHubs(), so pages share the list.
  const scopeType = session?.scope?.type;
  const multiHub = session?.role === "SUPER_ADMIN" || scopeType === "NETWORK" || scopeType === "CITY";
  const hubs = useQuery({ queryKey: ["hubs", null], queryFn: () => get("/org/hubs"), staleTime: 5 * 60_000, enabled: multiHub });
  const hubChoices = (hubs.data || []).filter((h) => scopeType !== "CITY" || !session?.cityId || h.cityId === session.cityId);
  // One hub to choose from: "All hubs" and that hub show the same data, so the
  // header names the hub instead of offering a pointless choice. A hub id left
  // over from before (another city, a hub since closed) is cleared.
  const onlyHub = hubs.isSuccess && hubChoices.length === 1 ? hubChoices[0] : null;
  useEffect(() => {
    if (onlyHub && hubId && hubId !== onlyHub.id) setHubId("");
  }, [onlyHub, hubId, setHubId]);

  // Global search goes where the role can look things up: patients for
  // desks, specimens / barcodes for the lab, orders for everyone else.
  const search = allowed("patients") ? { to: "/app/patients", ph: "Find a patient by name, mobile or UHID…" }
    : allowed("specimens") ? { to: "/app/specimens", ph: "Find a specimen by barcode or order…" }
    : allowed("orders") ? { to: "/app/orders", ph: "Find an order by number…" }
    : allowed("homecollection") ? { to: "/app/home-collection", ph: "Find a booking…" }
    : null;

  const canSeeAlerts = can(...CRITICAL_ROLES) && allowed("critical");
  const alerts = useCriticalAlerts({ acknowledged: "false", limit: 1 }, { enabled: canSeeAlerts, refetchInterval: 60_000 });
  const unack = alerts.data?.total ?? 0;

  const initials = (session?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  // Where the user works, in a word for the header: their city for city roles, else the
  // hub / center / partner they belong to; the menu shows the full path (scopeLine).
  const names = session?.scopeNames || {};
  const where = scopeType === "NETWORK" || session?.role === "SUPER_ADMIN" ? "All cities"
    : scopeType === "CITY" && names.city ? `${names.city} city`
    : names.center || names.b2bPartner || names.outsourceLab || names.radiologyPartner || names.hub || names.city || names.zone || "";
  const hubValue = hubId || "all";

  return (
    <header className="card-elev sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-border bg-card/85 px-3 backdrop-blur-md sm:h-16 sm:gap-3 sm:px-4 lg:px-5">
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open menu"
        data-testid="menu-btn"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-foreground/80 transition-colors hover:bg-muted lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>
      {/* Small screens: the search is a button that opens the patient search page. */}
      {search && (
        <button
          type="button"
          onClick={() => navigate(search.to)}
          aria-label="Search"
          data-testid="global-search-btn"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-foreground/80 transition-colors hover:bg-muted md:hidden"
        >
          <Search className="h-4 w-4" />
        </button>
      )}
      {search && (
        <form
          className="relative hidden w-full max-w-[340px] md:block"
          onSubmit={(e) => {
            e.preventDefault();
            if (!q.trim()) return;
            navigate(`${search.to}?q=${encodeURIComponent(q.trim())}`);
          }}
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            id="global-search"
            data-testid="global-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={search.ph}
            className="field h-10 pl-9 pr-16"
          />
          <kbd aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 hidden h-5 -translate-y-1/2 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground lg:flex">Ctrl K</kbd>
        </form>
      )}

      <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">
        {multiHub && onlyHub ? (
          <span data-testid="hub-selector" title={onlyHub.name} className="flex h-9 w-[124px] min-w-0 items-center gap-1.5 rounded-[8px] border border-border bg-card px-3 text-xs font-medium sm:w-[170px]">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" /><span className="truncate">{onlyHub.name}</span>
          </span>
        ) : multiHub ? (
          <Select value={hubValue} onValueChange={(v) => setHubId(v === "all" ? "" : v)} disabled={hubs.isLoading}>
            <SelectTrigger data-testid="hub-selector" className="h-9 w-[124px] min-w-0 rounded-[8px] text-xs sm:w-[170px]">
              <SelectValue placeholder="Hub" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{scopeType === "CITY" ? `All ${session?.scopeNames?.city || "city"} hubs` : "All hubs"}</SelectItem>
              {hubChoices.map((h) => (
                <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span data-testid="scope-chip" title={session?.scope?.label} className="hidden h-9 max-w-[260px] items-center gap-1.5 truncate rounded-full border border-border bg-card px-3 text-xs font-medium sm:flex">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" /><span className="truncate">{scopeLine(session)}</span>
          </span>
        )}

        <button
          data-testid="theme-toggle"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="hidden h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:flex"
          aria-label="Toggle theme"
        >
          <Sun className="h-4 w-4 dark:hidden" />
          <Moon className="hidden h-4 w-4 dark:block" />
        </button>

        {canSeeAlerts && (
          <button
            data-testid="notifications-btn"
            onClick={() => navigate("/app/critical-values")}
            className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={`${unack} unacknowledged critical alerts`}
            title={unack ? `${unack} critical value${unack === 1 ? "" : "s"} awaiting acknowledgement` : "No critical alerts pending"}
          >
            <Bell className="h-4 w-4" />
            {unack > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white ring-2 ring-background">
                {unack > 9 ? "9+" : unack}
              </span>
            )}
          </button>
        )}

        <div aria-hidden="true" className="hidden h-6 w-px bg-border lg:block" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="profile-menu" className="flex shrink-0 items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-muted">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{initials}</div>
              <div className="hidden max-w-[200px] text-left leading-tight md:block">
                <p className="truncate text-[13px] font-semibold text-foreground">{session?.name}</p>
                {/* Role and where it applies - the city, hub or center the user is assigned to. */}
                <p data-testid="profile-scope" title={`${session?.roleLabel || ""} · ${where}`} className="truncate text-[11px] text-muted-foreground">{session?.roleLabel}{where ? ` · ${where}` : ""}</p>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="label-caps">Signed in</DropdownMenuLabel>
            <div className="px-2 pb-2 text-xs">
              <p className="font-semibold">{session?.name}</p>
              <p className="text-muted-foreground">{session?.email}</p>
              <p className="mt-1 text-muted-foreground">{session?.roleLabel}</p>
              <p data-testid="menu-assigned" className="mt-2 flex items-start gap-1.5 rounded-md bg-muted px-2 py-1.5 text-foreground">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                <span><span className="text-muted-foreground">Assigned to </span><span className="font-medium">{scopeLine(session)}</span></span>
              </p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-my-access" onClick={() => navigate("/app/help")}>
              <LifeBuoy className="mr-2 h-4 w-4" /> My access &amp; password
            </DropdownMenuItem>
            <DropdownMenuItem className="sm:hidden" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              <Sun className="mr-2 h-4 w-4 dark:hidden" /><Moon className="mr-2 hidden h-4 w-4 dark:block" /> {theme === "dark" ? "Light theme" : "Dark theme"}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
